import { useEffect, useRef } from 'react';
import { ApolloError } from '@apollo/client';
import {
    BoundedContent,
    Button,
    EmptyState,
    EmptyStateActions,
    EmptyStateIllustration,
    EmptyStateTitle,
    FlexBox,
    H4,
    Spinner,
} from '@vp/swan';
import { bugtracker } from '@99designs/design-services-common';
import { useIdentityContext } from '@99designs/design-services-common';
import { DS_REQUEST_BRIEF_CREATE_URL, __ } from '@99designs/i18n';
import { useClaimBriefMutation, useCustomerIdentifiedMutation } from '../../graph';
import { isBifrostBrief } from '../BriefContext/isBifrostBrief';
import { StructuredBriefFragment, useBriefQuery } from '../BriefForm';
import { useHandleSubmit } from './useHandleSubmit';

interface SubmitProps {
    briefId: string;
    locale: string;
    willSkipCart: boolean;
}

export function Submit({ briefId, locale, willSkipCart }: SubmitProps) {
    const hasRun = useRef(false);
    const { authorizationHeader, shopperId, isSignedIn } = useIdentityContext();
    // The customerIdentified mutation is used to claim the request and brief for non-Bifrost briefs
    const [customerIdentified] = useCustomerIdentifiedMutation();
    // The claimBrief mutation is used to claim Bifrost briefs
    const [claimBrief] = useClaimBriefMutation({
        variables: { id: briefId },
    });

    const { data } = useBriefQuery({
        variables: {
            id: briefId,
        },
        errorPolicy: 'all',
        onError: (e: ApolloError) => {
            bugtracker.notify(`failed to get brief ${briefId} on Submit page: ${e.message}`);
            throw e;
        },
    });

    const { handleSubmit, state, loadingCopy, currentUrl, mpvId } = useHandleSubmit({
        briefId,
        locale,
        authorizationHeader,
        shopperId,
        willSkipCart,
    });

    const correlationId = data?.brief.correlationId;
    const isDuplicateBriefPurchaseAttempt = state === 'duplicate';

    const onViewProject = () => {
        window.location.replace(currentUrl);
    };

    const onStartNewBrief = () => {
        window.location.replace(`${DS_REQUEST_BRIEF_CREATE_URL}?mpvId=${mpvId}`);
    };

    // This ensures that handleSubmit is only called once, after the brief data is loaded and the request is claimed if necessary
    useEffect(() => {
        if (!data || hasRun.current || !isSignedIn) return;

        const claimAndSubmit = async () => {
            if (!data?.brief.request?.isClaimed) {
                if (correlationId) {
                    // customerIdentified mutation claims both the request and the brief
                    await customerIdentified({ variables: { requestId: correlationId } });
                } else if (isBifrostBrief(data.brief as StructuredBriefFragment)) {
                    // Bifrost briefs don't have a request, so we need to claim the brief directly
                    await claimBrief();
                }
            }

            void handleSubmit(data.brief);
            hasRun.current = true;
        };

        void claimAndSubmit();
    }, [claimBrief, customerIdentified, correlationId, data, handleSubmit, isSignedIn]);

    return (
        <BoundedContent p={7}>
            {isDuplicateBriefPurchaseAttempt ? (
                <EmptyState>
                    <EmptyStateIllustration illustrationType="error" />
                    <EmptyStateTitle>
                        {__('This design brief has already been purchased')}
                    </EmptyStateTitle>

                    <EmptyStateActions pt={4}>
                        <Button skin="secondary" onClick={onViewProject}>
                            {__('View project')}
                        </Button>
                        <Button skin="primary" onClick={onStartNewBrief}>
                            {__('Start new brief')}
                        </Button>
                    </EmptyStateActions>
                </EmptyState>
            ) : (
                <>
                    <FlexBox justifyContent={'center'} m={8}>
                        <Spinner size="super" accessibleText={__('Redirecting to the cart...')} />
                    </FlexBox>
                    <H4 p={'2'} textAlign={'center'}>
                        {loadingCopy}
                    </H4>
                </>
            )}
        </BoundedContent>
    );
}
